import { useCallback, useEffect, useRef, useState } from 'react';
import { usePaymentGateway } from './usePaymentGateway';
import {
  INITIAL_CHECK_DELAY_MS,
  POLL_INTERVAL_MS,
  STATUS_PAGO,
} from '../3ds/GetnetWebview3ds/constants';

type Args = {
  onSuccess: () => void;
  onError: (msg?: string) => void;
};

export function useCardProcessingPolling({ onSuccess, onError }: Args) {
  const { verificaStatusPagamentoCartao } = usePaymentGateway();
  const [isPolling, setIsPolling] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRunningRef = useRef(false);

  const clearPolling = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    isRunningRef.current = false;
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(
    (params: { compra_id?: string; ds_trans_id?: string }) => {
      if (isRunningRef.current) { return; }
      isRunningRef.current = true;
      setIsPolling(true);

      const poll = () => {
        verificaStatusPagamentoCartao(params)
          .then(({ data }) => {
            if (data.status === STATUS_PAGO) {
              clearPolling();
              onSuccess();
            }
          })
          .catch((err: any) => {
            clearPolling();
            onError(err?.response?.data?.msg);
          });
      };

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
      }, INITIAL_CHECK_DELAY_MS);
    },
    [verificaStatusPagamentoCartao, onSuccess, onError, clearPolling],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); }
      if (intervalRef.current) { clearInterval(intervalRef.current); }
      isRunningRef.current = false;
    };
  }, []);

  return { startPolling, isPolling, clearPolling };
}
